import {
  SettingEntityType,
  SettingKey,
  useSetting,
} from '@commenda/api';

interface UseAccountManagerCallEnabledReturn {
  isLoading: boolean;
  /**
   * Whether the "Schedule a call with your account manager" CTA should show
   * for this company.
   */
  enabled: boolean;
}

/**
 * Gate for the customer-facing "Book a call with your account manager"
 * feature, controlled per-company by CSMs/AMs from the admin panel.
 *
 * Default-ON semantics: the CTA is enabled unless an admin has explicitly
 * turned it off (`setting === false`). An unset/null setting (the common
 * case — no admin has touched it), a still-loading value, or a missing
 * `companyId` all resolve to enabled. Returning enabled during load avoids
 * the CTA flickering out then back in for the (overwhelmingly common)
 * enabled case.
 *
 * The single source of truth for both render sites (the home Point-of-contact
 * card and the app-wide Contact-us popover), so the gating logic can never
 * drift between them.
 */
export function useAccountManagerCallEnabled(
  companyId: number | null | undefined
): UseAccountManagerCallEnabledReturn {
  const { setting, isLoading } = useSetting({
    key: SettingKey.BOOK_CALL_WITH_ACCOUNT_MANAGER,
    entities: companyId
      ? [{ entityType: SettingEntityType.COMPANY, entityId: companyId }]
      : [],
  });

  return {
    isLoading,
    // `setting` is `boolean | null | undefined`: null = explicitly unset,
    // undefined = loading/no-fetch. Only an explicit `false` disables.
    enabled: setting !== false,
  };
}
